// hooks/useClickOutside.ts

import { useEffect, useRef, RefObject } from 'react';

/**
 * Hook that detects clicks outside of specified element(s) and calls a handler.
 *
 * Supports single or multiple refs (e.g., a popover + its toggle button).
 * Only fires when all refs are mounted and the click target is outside all of them.
 *
 * @param refs - Single ref or array of refs to track. Clicks outside all of these trigger onClose.
 * @param onClose - Callback invoked when a click outside is detected.
 * @param isActive - Whether the listener is active. Defaults to true.
 * @param options - `includeTouch` also closes on `touchstart`, for overlays
 *                  (e.g. the mobile drawer) that must dismiss on the touch
 *                  rather than waiting for the synthesized mousedown.
 *
 * @example
 * // Basic usage — always listening
 * useClickOutside(dropdownRef, () => setIsOpen(false));
 *
 * @example
 * // Conditional — only listen when open
 * useClickOutside(menuRef, () => setIsOpen(false), isOpen);
 *
 * @example
 * // Multiple refs — popover + toggle button
 * useClickOutside([popoverRef, buttonRef], () => setIsOpen(false), isOpen);
 *
 * @example
 * // Touch-aware — mobile drawer
 * useClickOutside(drawerRef, closeDrawer, isDrawerOpen, { includeTouch: true });
 */
export interface UseClickOutsideOptions {
  /** Also listen for `touchstart`. Defaults to false (mousedown only). */
  includeTouch?: boolean;
}

export function useClickOutside(
  refs: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[],
  onClose: () => void,
  isActive: boolean = true,
  options: UseClickOutsideOptions = {}
): void {
  const { includeTouch = false } = options;
  // Store callback in ref to avoid re-subscribing on every render
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Store refs in a ref to avoid dependency issues with arrays
  const refsRef = useRef(refs);
  refsRef.current = refs;

  useEffect(() => {
    if (!isActive) return;

    const handleClickOutside = (event: Event) => {
      const currentRefs = Array.isArray(refsRef.current)
        ? refsRef.current
        : [refsRef.current];

      // All refs must be mounted for the check to proceed
      const allMounted = currentRefs.every(ref => ref.current !== null);
      if (!allMounted) return;

      // Close if click is outside ALL tracked elements
      const isOutside = currentRefs.every(
        ref => !ref.current!.contains(event.target as Node)
      );
      if (isOutside) {
        onCloseRef.current();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    if (includeTouch) {
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isActive, includeTouch]);
}
